/**
 * Prescription utility functions for AI Prescription
 */

import { AIPrescriptionPayload } from "../../Apis/AIPrescriptionApis";
import { buildOrchestrationProcessPayload } from "../orchestrationPayload";
import { getPatientId, getPatientName } from "../patientSession";
import { sanitizeValue } from "../textUtils";
import { STORAGE_KEYS } from "./constants";
import { getPrescriptionFlags } from "./prescriptionFlagsStore";
import { consumePendingSendOrigin } from "./sendOriginStore";

/** In-memory patient display name (not persisted to browser storage). */
export function getResolvedPatientNameForOrchestration(): string {
  return getPatientName();
}

type PendingSelectedPharmacy = {
  pharmacyId?: string;
  pharmacyName?: string;
  medicationName?: string;
  selectionText?: string;
};

const normalizePendingSelectionText = (value: string): string =>
  (value || "").trim().replace(/\s+/g, " ");

const consumePendingSelectedPharmacy = (text: string): PendingSelectedPharmacy | null => {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  const raw = sessionStorage.getItem(STORAGE_KEYS.PENDING_SELECTED_PHARMACY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PendingSelectedPharmacy;
    const selectionText = normalizePendingSelectionText(parsed.selectionText || "");
    const payloadText = normalizePendingSelectionText(text);
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_SELECTED_PHARMACY);
    if (selectionText && payloadText && selectionText !== payloadText) {
      return null;
    }
    if (!parsed.pharmacyId && !parsed.pharmacyName) {
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_SELECTED_PHARMACY);
    return null;
  }
};

/**
 * Consume the favourite-drug chip's full structured object stashed by
 * rememberPendingSelectedFavouriteDrug (handlers.ts), if the outgoing text still matches the
 * selection that was made. Threaded into the WS payload as `favouriteDrug` so the backend can
 * build a ready-to-confirm draft directly instead of re-deriving it via
 * extract_medication_info + get_search_pharmacies.
 */
const consumePendingSelectedFavouriteDrug = (text: string): Record<string, unknown> | null => {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  const raw = sessionStorage.getItem(STORAGE_KEYS.PENDING_SELECTED_FAVOURITE_DRUG);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { drug?: Record<string, unknown>; selectionText?: string };
    const selectionText = normalizePendingSelectionText(parsed.selectionText || "");
    const payloadText = normalizePendingSelectionText(text);
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_SELECTED_FAVOURITE_DRUG);
    if (selectionText && payloadText && selectionText !== payloadText) {
      return null;
    }
    if (!parsed.drug || typeof parsed.drug !== "object") {
      return null;
    }
    return parsed.drug;
  } catch {
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_SELECTED_FAVOURITE_DRUG);
    return null;
  }
};

/** Non-negative refill count; default 0 (matches server/orchestration coercion). */
export const normalizeRefills = (med: any): number => {
  const v = med?.refills;
  if (v === null || v === undefined) return 0;
  if (typeof v === 'boolean') return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.floor(v));
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (!s || s === 'null' || s === 'none' || s === 'n/a' || s === 'nr') return 0;
    const m = s.match(/\d+/);
    if (m) return Math.max(0, parseInt(m[0], 10));
  }
  return 0;
};

/**
 * Validates if a medication item has at least one valid field
 * @param med - The medication item to validate
 * @returns true if at least one field (name, dosage, sig, quantity, route, dosage_form / qualifier_types) is valid
 */
export const isValidMedicationItem = (med: any): boolean => {
  const hasName = med?.medication_name && typeof med.medication_name === 'string' && med.medication_name.trim() !== '';
  const hasDosage = med?.dosage && typeof med.dosage === 'string' && med.dosage.trim() !== '';
  const hasSig = med?.SIG_value && typeof med.SIG_value === 'string' && med.SIG_value.trim() !== '';
  const hasQuantity = med?.medication_quantity && typeof med.medication_quantity === 'string' && med.medication_quantity.trim() !== '';
  const hasRoute = med?.route_of_administration && typeof med.route_of_administration === 'string' && med.route_of_administration.trim() !== '';
  const dosageFormRaw = med?.dosage_form ?? med?.qualifier_types;
  const hasDosageForm =
    dosageFormRaw && typeof dosageFormRaw === 'string' && dosageFormRaw.trim() !== '';
  return hasName || hasDosage || hasSig || hasQuantity || hasRoute || hasDosageForm;
};

/**
 * Creates prescription objects from medication data array
 * @param medicationData - Array of medication data objects
 * @param options - Optional configuration including pharmacy, substitutions, aiResponse, etc.
 * @returns Array of prescription objects
 */
export const createPrescriptionsFromMedicationData = (
  medicationData: any[],
  options?: {
    substitutions?: string;
    pharmacy?: string;
    aiResponse?: string;
    existingPrescriptions?: any[];
    index?: number;
    defaultPharmacy?: string;
  }
) => {
  const defaultPharmacy = options?.defaultPharmacy || options?.pharmacy || "";
  return medicationData.map((med: any, index: number) => {
    const dosageForm = (med.dosage_form ?? med.qualifier_types) || "";
    return {
    id: Date.now() + index,
    keywords: [med.medication_name?.toLowerCase() || ""],
    drugName: `${med.medication_name || ""} ${med.dosage || ""} ${med.route_of_administration || ""} ${dosageForm}`.trim(),
    medication_name: med.medication_name || "",
    dosage: med.dosage || "",
    route_of_administration: med.route_of_administration || "",
    dosage_form: dosageForm,
    sig: sanitizeValue(med.SIG_value),
    quantity: sanitizeValue(med.medication_quantity),
    refills: normalizeRefills(med),
    // drugPopulate-derived fields for the summary card (EPA / Priority Indicator / Coverage).
    epaFlag: med.epaFlag === true,
    priorAuthorization: med.priorAuthorization === true,
    priorityIndicator: med.priorityIndicator === true,
    coverage: typeof med.coverage === "string" ? med.coverage : undefined,
    scheduleDrugFlag:
      med.scheduleDrugFlag === true ||
      (med.matched_drug && med.matched_drug.scheduleDrugFlag === true),
    deaClassCode: (() => {
      const raw = med.deaClassCode ?? med.matched_drug?.deaClassCode;
      if (raw == null || raw === "") return undefined;
      const s = String(raw).trim();
      return s && s.toLowerCase() !== "null" ? s : undefined;
    })(),
    epcsQueued: med.epcsQueued === true,
    // Prioritize explicitly provided substitutions over existing ones
    substitutions: options?.substitutions !== undefined 
      ? options.substitutions 
      : (options?.existingPrescriptions?.[index]?.substitutions || "Substitutions allowed"),
    // A fresh, backend-resolved summary_pharmacy_name for THIS turn must win over whatever
    // carried-forward text sits in existingPrescriptions[index].pharmacy -- otherwise the raw,
    // unresolved text a user typed into the inline-edit pharmacy field (e.g. "cvs pharmacy")
    // gets baked into existingPrescriptions on the turn it's echoed back, and then wins forever
    // on every later turn even after the backend resolves it to a real pharmacy (e.g. "CVS
    // Pharmacy #68568"), since it was checked first.
    pharmacy: (med as any).summary_pharmacy_name
      || options?.existingPrescriptions?.[index]?.pharmacy
      || options?.pharmacy
      || defaultPharmacy,
    // Already sent to a pharmacy this session — render read-only (no Send button).
    sent: med.sent === true,
    airesponse: options?.aiResponse || "",
  };
  });
};

/**
 * When medication options still need a choice, defer pharmacy pickers so the user resolves one flow at a time.
 *
 * When the server includes a parallel pharmacy selection (multi-intent with
 * ``pharmacy_suggestions``), show both pickers together instead of hiding pharmacy.
 */
export interface MedicationSuggestionGroup {
  medication_name: string;
  options: string[];
}

/** Build per-medication picker groups from nested ``medication_data`` (unmatched rows only). */
export const medicationSuggestionGroupsFromMedicationData = (
  medicationData?: unknown[]
): MedicationSuggestionGroup[] => {
  if (!Array.isArray(medicationData)) {
    return [];
  }
  const groups: MedicationSuggestionGroup[] = [];
  for (const med of medicationData) {
    if (!med || typeof med !== "object") {
      continue;
    }
    const row = med as Record<string, unknown>;
    if (row.matched_drug) {
      continue;
    }
    const sd = row.suggestions_data;
    if (!Array.isArray(sd) || sd.length === 0) {
      continue;
    }
    const options: string[] = [];
    for (const item of sd) {
      if (!item || typeof item !== "object") {
        continue;
      }
      const sug = item as Record<string, unknown>;
      const desc = String(
        sug.drugInfoDescription || sug.drugName || ""
      ).trim();
      if (desc && !options.includes(desc)) {
        options.push(desc);
      }
    }
    if (options.length === 0) {
      continue;
    }
    const name = String(row.medication_name || "Medication").trim() || "Medication";
    groups.push({ medication_name: name, options });
  }
  return groups;
};

/** Prefer server ``suggestion_groups``; else derive from ``medication_data``. */
export const medicationSuggestionGroupsFromPayload = (
  data?: Record<string, unknown>,
  medicationData?: unknown[]
): MedicationSuggestionGroup[] => {
  const raw = data?.suggestion_groups;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .map((g) => {
        if (!g || typeof g !== "object") {
          return null;
        }
        const row = g as Record<string, unknown>;
        const name = String(row.medication_name || "Medication").trim() || "Medication";
        const opts = Array.isArray(row.options)
          ? row.options.map((o) => String(o).trim()).filter(Boolean)
          : [];
        return opts.length ? { medication_name: name, options: opts } : null;
      })
      .filter((g): g is MedicationSuggestionGroup => g !== null);
  }
  return medicationSuggestionGroupsFromMedicationData(medicationData);
};

export const flatLabelsFromSuggestionGroups = (
  groups: MedicationSuggestionGroup[]
): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const g of groups) {
    for (const opt of g.options) {
      if (opt && !seen.has(opt)) {
        seen.add(opt);
        out.push(opt);
      }
    }
  }
  return out;
};

export interface PharmacySuggestionGroup {
  medication_name: string;
  options: any[];
  /**
   * `pharmacy_search_fallback_mode` scoped to THIS medication's own search (compound turns can
   * search a different pharmacy name per drug, so each can land on a different tier — one may
   * be a clean local match while a sibling falls to a nationwide search). Drives this group's
   * own advisory banner in PharmacyLookup, independent of any other group's mode.
   */
  fallback_mode?: string | null;
}

const _pharmacyLabel = (item: unknown): string => {
  if (typeof item === "string") return item.trim();
  if (!item || typeof item !== "object") return "";
  const p = item as Record<string, unknown>;
  return String(
    p.pharmacyName ||
      p.nameConcatenatedWithAddress ||
      p.name ||
      p.pharmacy_name ||
      p.pharmacyId ||
      ""
  ).trim();
};

export const pharmacySuggestionGroupsFromPayload = (
  data?: Record<string, unknown>,
  medicationData?: unknown[]
): PharmacySuggestionGroup[] => {
  const raw = data?.pharmacy_suggestion_groups;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .map((g) => {
        if (!g || typeof g !== "object") return null;
        const row = g as Record<string, unknown>;
        const name = String(row.medication_name || "Medication").trim() || "Medication";
        const opts = Array.isArray(row.options)
          ? row.options.filter((o) => _pharmacyLabel(o))
          : [];
        const fallbackMode =
          typeof row.fallback_mode === "string" && row.fallback_mode.trim()
            ? row.fallback_mode.trim()
            : null;
        return opts.length ? { medication_name: name, options: opts, fallback_mode: fallbackMode } : null;
      })
      .filter((g): g is PharmacySuggestionGroup => g !== null);
  }

  const meds = Array.isArray(medicationData)
    ? medicationData
    : Array.isArray(data?.medication_data)
      ? (data?.medication_data as unknown[])
      : [];
  const groups: PharmacySuggestionGroup[] = [];
  for (const med of meds) {
    if (!med || typeof med !== "object") continue;
    const row = med as Record<string, unknown>;
    const rawPharmacies = row.pharmacy_suggestions_data;
    if (!Array.isArray(rawPharmacies) || rawPharmacies.length === 0) continue;
    const opts = rawPharmacies.filter((p) => _pharmacyLabel(p));
    if (!opts.length) continue;
    const name = String(row.medication_name || "Medication").trim() || "Medication";
    const fallbackMode =
      typeof row.pharmacy_search_fallback_mode === "string" && row.pharmacy_search_fallback_mode.trim()
        ? row.pharmacy_search_fallback_mode.trim()
        : null;
    groups.push({ medication_name: name, options: opts, fallback_mode: fallbackMode });
  }
  return groups;
};

const _medicationNameKey = (name: unknown): string => {
  const s = String(name ?? "").trim().toLowerCase();
  if (!s) return "";
  return s.split(/\s+/)[0]?.replace(/[^a-z0-9-]/g, "") ?? "";
};

/** Count draft rows that already have a catalog ``matched_drug`` (compound multi-Rx). */
export const countMedicationsWithMatchedDrug = (
  structuredMedicationData?: unknown[]
): number => {
  if (!Array.isArray(structuredMedicationData)) {
    return 0;
  }
  return structuredMedicationData.filter(
    (m) => m && typeof m === "object" && (m as { matched_drug?: unknown }).matched_drug
  ).length;
};

/**
 * Compound multi-Rx draft (2+ lines). Summary and interaction surfaces should not
 * narrow to the RTF primary drug alone.
 */
export const isMultiRxPrescriptionDraft = (
  structuredMedicationData?: unknown[],
  prescriptions?: unknown[]
): boolean => {
  if (countMedicationsWithMatchedDrug(structuredMedicationData) >= 2) {
    return true;
  }
  if (Array.isArray(prescriptions) && prescriptions.length >= 2) {
    return true;
  }
  if (Array.isArray(structuredMedicationData)) {
    const namedRows = structuredMedicationData.filter(
      (m) =>
        m &&
        typeof m === "object" &&
        String((m as { medication_name?: string }).medication_name || "").trim()
    );
    if (namedRows.length >= 2) {
      return true;
    }
  }
  return false;
};

/** @deprecated Use {@link isMultiRxPrescriptionDraft} */
export const shouldShowAllHighInteractionsForCompoundDraft = (
  structuredMedicationData?: unknown[],
  prescriptions?: unknown[]
): boolean => isMultiRxPrescriptionDraft(structuredMedicationData, prescriptions);

const _prescriptionMatchesMedicationRow = (
  prescription: Record<string, unknown>,
  med: Record<string, unknown>
): boolean => {
  const medKey = _medicationNameKey(med.medication_name);
  if (!medKey) return false;
  const candidates = [
    prescription.medication_name,
    prescription.drugName,
    prescription.title,
    prescription.label,
    prescription.name,
  ];
  const snap = prescription.summary_snapshot;
  if (snap && typeof snap === "object") {
    candidates.push((snap as { medication_name?: string }).medication_name);
  }
  return candidates.some((c) => {
    const ck = _medicationNameKey(c);
    if (!ck) return false;
    return ck === medKey;
  });
};

/**
 * During strength selection, show summary cards only for meds already catalog-locked
 * (e.g. Celexa while Clarithromycin still has ``suggestions_data``).
 */
export const prescriptionsForLockedMedications = (
  prescriptions: unknown[],
  structuredMedicationData?: unknown[]
): unknown[] => {
  if (!Array.isArray(prescriptions) || prescriptions.length === 0) {
    return [];
  }
  if (!Array.isArray(structuredMedicationData)) {
    return prescriptions;
  }
  const lockedRows = structuredMedicationData.filter(
    (m) =>
      m &&
      typeof m === "object" &&
      (m as { matched_drug?: unknown }).matched_drug
  ) as Record<string, unknown>[];
  if (lockedRows.length === 0) {
    return [];
  }
  return prescriptions.filter((p) => {
    if (!p || typeof p !== "object") return false;
    const pres = p as Record<string, unknown>;
    return lockedRows.some((med) => _prescriptionMatchesMedicationRow(pres, med));
  });
};

export interface FilterPrescriptionsForSummaryOptions {
  highSeverityIntent?: boolean;
  highSeverityPrimaryMedName?: string;
  highSeverityReviewResolved?: boolean;
  structuredMedicationData?: unknown[];
  prescriptionMatchesPrimaryName?: (prescription: unknown, primary: string) => boolean;
}

const _isEmptyRequiredValue = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  const s = String(v).trim().toLowerCase();
  return s === "" || s === "none" || s === "null";
};

/**
 * Mirrors the server's base required-fields check (orchestration_agent's
 * BASE_REQUIRED_FIELDS / _has_required_med_fields) so a row only counts as "ready" here when
 * it would also be READY_TO_CONFIRM server-side: catalog-locked (matched_drug) AND every plain
 * text field filled in. Does not need to be byte-identical to the server's per-medication
 * required-fields function (e.g. scheduled-drug extras) -- this only decides what's SHOWN in
 * the summary; the server's own gate is still the one deciding whether ui_blocks includes
 * "summary" at all.
 */
const _medicationRowIsReady = (med: Record<string, unknown>): boolean => {
  if (!med.matched_drug) return false;
  const dosageForm = med.dosage_form ?? med.qualifier_types;
  return [
    med.medication_name,
    med.route_of_administration,
    dosageForm,
    med.medication_quantity,
    med.SIG_value,
    med.dosage,
  ].every((v) => !_isEmptyRequiredValue(v));
};

/**
 * Prescription summary list for display. High-severity RTF may focus alternates on one
 * drug, but every draft line stays visible unless a picker flow hides the summary
 * (medication or pharmacy suggestions — handled in AIResponseBox).
 *
 * One additional narrowing: in a mixed multi-Rx draft where at least one line is fully
 * ready (catalog-locked, every field filled) and at least one sibling line still isn't, show
 * only the ready line(s). The incomplete sibling is not lost -- it stays in
 * structuredMedicationData/prescriptions for later turns -- it just isn't surfaced in THIS
 * summary until it's resolved (matches prescriptionsForLockedMedications' existing pattern for
 * the "one locked + one needs a suggestion pick" case, generalized to "needs any field").
 */
export const filterPrescriptionsForSummaryDisplay = (
  prescriptions: unknown[],
  _options?: FilterPrescriptionsForSummaryOptions
): unknown[] => {
  if (!Array.isArray(prescriptions)) return [];
  const structured = _options?.structuredMedicationData;
  if (!Array.isArray(structured) || structured.length < 2) {
    // Nothing to split on (no structured data, or a single-medication draft) -- unchanged.
    return prescriptions;
  }
  const rows = structured.filter(
    (m): m is Record<string, unknown> =>
      !!m && typeof m === "object" && (m as { type?: unknown }).type !== "pharmacy_choice"
  );
  const readyRows = rows.filter(_medicationRowIsReady);
  const notReadyRows = rows.filter((m) => !_medicationRowIsReady(m));
  if (readyRows.length === 0 || notReadyRows.length === 0) {
    // Whole draft ready, or whole draft still incomplete -- nothing to hide either way.
    return prescriptions;
  }
  const filtered = prescriptions.filter((p) => {
    if (!p || typeof p !== "object") return false;
    return readyRows.some((med) =>
      _prescriptionMatchesMedicationRow(p as Record<string, unknown>, med)
    );
  });
  // Fail open: never show an empty summary just because name-matching didn't line up.
  return filtered.length > 0 ? filtered : prescriptions;
};

export const shouldDeferPharmacyForMedicationSuggestions = (
  structuredMedicationData?: any[],
  suggestions?: (string | any)[],
  options?: { allowSimultaneousPharmacyPicker?: boolean }
): boolean => {
  if (options?.allowSimultaneousPharmacyPicker) {
    return false;
  }
  const nestedUnresolved =
    Array.isArray(structuredMedicationData) &&
    structuredMedicationData.some(
      (m) =>
        m &&
        typeof m === 'object' &&
        !m.matched_drug &&
        Array.isArray(m.suggestions_data) &&
        m.suggestions_data.length > 0
    );
  const topLevelDrugSuggestions = Array.isArray(suggestions) && suggestions.length > 0;
  return nestedUnresolved || topLevelDrugSuggestions;
};

/**
 * Creates API payload for prescription processing.
 * Draft medication state is server-owned; do not attach client medication arrays.
 * @param text - The prescription text
 * @param options - Optional `vttContent` (WebVTT body); optional `sessionId` override (else from session storage / WS `connected`)
 */
export const createPayload = (
  text: string,
  options?: { vttContent?: string; sessionId?: string }
): AIPrescriptionPayload => {
  const trimmedVtt = options?.vttContent?.trim();
  const sid = options?.sessionId?.trim();
  const rxFlags = getPrescriptionFlags(getPatientId());
  const hasRxFlags = rxFlags && Object.keys(rxFlags).length > 0;
  const doctorId = (localStorage.getItem("doctorId") || "").trim();
  const loginUserId = (localStorage.getItem("LoginUserID") || "").trim();
  const patientName = getResolvedPatientNameForOrchestration();
  const selectedPharmacy = consumePendingSelectedPharmacy(text);
  const favouriteDrug = consumePendingSelectedFavouriteDrug(text);
  const sendOrigin = consumePendingSendOrigin(text);
  const built = buildOrchestrationProcessPayload({
    type: "process",
    text,
    patientUniqueIdentifier: getPatientId(),
    patientName,
    doctorId,
    providerLocationId:
      (typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("clinicId")) ||
      "",
    sessionId: sid,
    loginUserId,
    vttContent: trimmedVtt,
    selectedPharmacy: selectedPharmacy
      ? {
          pharmacyId: selectedPharmacy.pharmacyId || "",
          pharmacyName: selectedPharmacy.pharmacyName || "",
          medicationName: selectedPharmacy.medicationName || "",
        }
      : undefined,
    pharmacyId: selectedPharmacy?.pharmacyId || undefined,
    pharmacyName: selectedPharmacy?.pharmacyName || undefined,
    favouriteDrug: favouriteDrug || undefined,
    sendOrigin,
  });
  return {
    text,
    patientUniqueIdentifier: String(built.patientUniqueIdentifier || ""),
    patientName: built.patientName as string | undefined,
    jwtToken: String(built.jwtToken || ""),
    doctorId: String(built.doctorId || ""),
    providerLocationId: String(built.providerLocationId || ""),
    sessionId: built.sessionId as string | undefined,
    loginUserId: built.loginUserId as string | undefined,
    vttContent: built.vttContent as string | undefined,
    selectedPharmacy: built.selectedPharmacy as AIPrescriptionPayload["selectedPharmacy"],
    pharmacyId: built.pharmacyId as string | undefined,
    pharmacyName: built.pharmacyName as string | undefined,
    favouriteDrug: built.favouriteDrug as Record<string, unknown> | undefined,
    prescriptionFlags: hasRxFlags
      ? (rxFlags as AIPrescriptionPayload["prescriptionFlags"])
      : undefined,
    sendOrigin: built.sendOrigin as "button" | undefined,
  };
};

/**
 * Checks if a prescription was successfully sent based on API response text
 * @param apiResponse - The API response text
 * @returns true if response indicates prescription was sent
 */
export const checkPrescriptionSent = (apiResponse: string): boolean => {
  if (!apiResponse || typeof apiResponse !== 'string') return false;
  const lower = apiResponse.toLowerCase();
  return lower.includes('successfully sent') || 
         lower.includes('sent to the pharmacy') ||
         lower.includes('prescription has been sent') ||
         lower.includes('has been successfully sent') ||
         lower.includes('sent to pharmacy');
};

/**
 * Creates an empty medication data structure
 * @returns Array with one empty medication object
 */
export const createEmptyMedicationData = () => {
  return [{
    SIG_value: "",
    dosage: "",
    medication_name: "",
    medication_quantity: "",
    dosage_form: "",
    route_of_administration: "",
    refills: 0,
  }];
};

/**
 * Merges new API result with existing medication data
 * Preserves existing fields and only updates fields present in the new result
 * @param existingData - Existing medication data array
 * @param newResult - New medication data from API
 * @returns Merged medication data array
 */
export const mergeMedicationData = (existingData: any[], newResult: any[]): any[] => {
  if (!existingData || existingData.length === 0) {
    return newResult;
  }
  
  if (!newResult || newResult.length === 0) {
    return existingData;
  }
  
  // Merge each item by index
  const merged = existingData.map((existingItem, index) => {
    const newItem = newResult[index];
    if (newItem) {
      return { ...existingItem, ...newItem };
    }
    return existingItem;
  });
  
  // If new result has more items than existing, add them
  if (newResult.length > existingData.length) {
    for (let i = existingData.length; i < newResult.length; i++) {
      merged.push(newResult[i]);
    }
  }
  
  return merged;
};

/**
 * Parses API result into an array format
 * Handles various formats: array, string (JSON), single object
 * @param result - The result to parse
 * @returns Array of medication data objects
 */
export const parseAPIResult = (result: any): any[] => {
  try {
    if (Array.isArray(result)) {
      return result;
    } else if (typeof result === 'string') {
      const parsed = JSON.parse(result);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return [parsed];
      }
      return Array.isArray(parsed) ? parsed : [parsed];
    } else if (result && typeof result === 'object') {
      return [result];
    } else {
      return result ? [result] : [];
    }
  } catch (error) {
    console.error("Error parsing result:", error);
    return [];
  }
};

